import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { MagnifyingGlass, Fire, ArrowRight, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Two side-by-side widgets on the admin Overview:
 * 1. Top 5 empty searches (last 7 days) — click a query to open the full panel.
 * 2. Priority prospects (from search-hit + search-miss signals).
 */
export default function InsightsWidgets({ onGoToSearchMisses }) {
  const [misses, setMisses] = useState(null);
  const [priority, setPriority] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/search-misses", { params: { days: 7, limit: 5 } }).then((r) => setMisses(r.data)).catch(() => setMisses({ items: [], total_distinct: 0 })),
    api.get("/admin/priority-prospects/stats").then((r) => setPriority(r.data)).catch(() => setPriority({ total: 0, top: [], hits_last_7d: 0 })),
  ]);
  useEffect(() => { load(); }, []);

  const rebuild = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/priority-prospects/rebuild");
      toast.success("Priorités téléprospection recalculées");
      await load();
    } catch {
      toast.error("Erreur recalcul");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="insights-widgets">
      {/* Widget 1 — Empty searches */}
      <div className="bg-card border border-border rounded-sm p-4" data-testid="widget-empty-searches">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="label-uppercase text-primary flex items-center gap-1.5">
              <MagnifyingGlass size={12} weight="bold"/> Top 5 recherches vides (7 j)
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Ce que les visiteurs cherchent sans trouver — combler = plus de trafic capté.
            </div>
          </div>
          <button
            onClick={onGoToSearchMisses}
            className="text-primary hover:text-[#0f3d29] text-[11px] font-semibold inline-flex items-center gap-0.5 shrink-0"
            data-testid="empty-searches-cta"
          >
            Tout voir <ArrowRight size={10} weight="bold"/>
          </button>
        </div>
        {!misses ? (
          <div className="text-xs text-muted-foreground py-4">Chargement…</div>
        ) : (misses.items || []).length === 0 ? (
          <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 text-center">
            <div className="text-sm">🎉 <b>Toutes les recherches trouvent au moins un résultat.</b></div>
            <div className="text-[11px] text-muted-foreground mt-1">Aucun besoin non couvert cette semaine — l&apos;annuaire est complet.</div>
          </div>
        ) : (
          <ol className="space-y-1.5" data-testid="empty-searches-list">
            {misses.items.slice(0, 5).map((it, i) => (
              <li key={it.query} className="flex items-center gap-2 text-sm bg-accent/20 rounded-sm px-2.5 py-1.5" data-testid={`empty-search-${it.query}`}>
                <span className="w-5 h-5 shrink-0 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 truncate">« {it.query} »</span>
                <span className="text-[11px] tabular-nums font-bold text-foreground bg-accent px-2 py-0.5 rounded-sm">{it.count}×</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Widget 2 — Priority prospects */}
      <div className="bg-card border border-border rounded-sm p-4" data-testid="widget-priority-prospects">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="label-uppercase text-secondary flex items-center gap-1.5">
              <Fire size={12} weight="fill"/> Priorité téléprospection
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Fiches boostées en tête de la prochaine liste télépro (recherches consommées + recherches vides couvertes).
            </div>
          </div>
          <button
            onClick={rebuild}
            disabled={refreshing}
            className="text-secondary hover:text-[#a35d42] text-[11px] font-semibold inline-flex items-center gap-0.5 shrink-0 disabled:opacity-50"
            data-testid="priority-rebuild-btn"
            title="Recalcule immédiatement les priorités (sinon exécuté à 05:45 UTC chaque matin)"
          >
            <ArrowClockwise size={10} weight="bold" className={refreshing ? "animate-spin" : ""}/> Recalculer
          </button>
        </div>
        {!priority ? (
          <div className="text-xs text-muted-foreground py-4">Chargement…</div>
        ) : priority.total === 0 ? (
          <div className="bg-accent/20 border border-dashed border-border rounded-sm p-4 text-center">
            <div className="text-sm">Aucune fiche prioritaire pour le moment.</div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Les priorités se remplissent quand des visiteurs recherchent sur l&apos;annuaire ({priority.hits_last_7d || 0} hits sur 7 j).
            </div>
          </div>
        ) : (
          <>
            <div className="text-[11px] text-muted-foreground mb-2">
              <b className="text-foreground tabular-nums">{priority.total}</b> fiches prioritaires · <b className="text-foreground tabular-nums">{priority.hits_last_7d}</b> recherches capturées sur 7 j
            </div>
            <ol className="space-y-1.5" data-testid="priority-prospects-list">
              {priority.top.slice(0, 5).map((p, i) => (
                <li key={p.business_id} className="flex items-center gap-2 text-sm bg-secondary/5 rounded-sm px-2.5 py-1.5" data-testid={`priority-item-${p.business_id}`}>
                  <span className="w-5 h-5 shrink-0 rounded-full bg-secondary/15 text-secondary text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{p.business_name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{p.business_city} · {(p.reasons || []).map((r) => r.label).join(" · ")}</div>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </div>
  );
}
